import { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input, HelperText } from './ui';
import { loadDiscoveryDataset } from '../lib/schoolDiscovery';
import type { DiscoverySchool } from '../lib/schoolDiscoveryTypes';

/**
 * Result shape emitted by SchoolSearch. Kept identical to the old
 * SchoolSearchResult so callers (the Profile target-school editor) need no
 * change. `scorecard_id` is the College Scorecard institution id.
 */
export interface SchoolSearchResult {
  scorecard_id: number;
  name: string;
  city: string | null;
  state: string | null;
}

interface Props {
  /** Called when the user picks a school from the dropdown. */
  onSelect: (result: SchoolSearchResult) => void;
  /** Optional initial query (e.g., the school name already entered). */
  initialQuery?: string;
}

/**
 * A search box for picking a school. As of v0.2.33 this searches the bundled
 * School Discovery dataset (the same data the Schools tab uses) — NOT the live
 * College Scorecard API. So it is instant, works offline, and needs no API
 * key. The whole app now has one school data source.
 */
export function SchoolSearch({ onSelect, initialQuery = '' }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [allSchools, setAllSchools] = useState<DiscoverySchool[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [results, setResults] = useState<SchoolSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [noMatches, setNoMatches] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the bundled dataset once (cached in the lib after first load).
  useEffect(() => {
    loadDiscoveryDataset()
      .then((d) => setAllSchools(d.schools))
      .catch(() => setLoadError(true));
  }, []);

  // Filter the bundled list as the user types — instant, no debounce needed.
  useEffect(() => {
    if (!allSchools) return;
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      setResults([]);
      setNoMatches(false);
      return;
    }
    const matches = allSchools
      .filter((s) => {
        const hay = `${s.name} ${s.city ?? ''} ${s.state ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 30)
      .map((s) => ({
        scorecard_id: s.id,
        name: s.name,
        city: s.city,
        state: s.state,
      }));
    setResults(matches);
    setNoMatches(matches.length === 0);
    setOpen(true);
  }, [query, allSchools]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function pick(r: SchoolSearchResult) {
    onSelect(r);
    setQuery(`${r.name}${r.city ? ` (${r.city}${r.state ? `, ${r.state}` : ''})` : ''}`);
    setOpen(false);
  }

  if (loadError) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
        <strong>Search unavailable:</strong> the school data couldn't be loaded.
        You can still enter school details manually below.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-soft pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          placeholder="Search by school name (e.g., High Point University)"
          className="!pl-8"
        />
        {!allSchools && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-soft animate-spin" />
        )}
      </div>
      <HelperText>
        Search US colleges. Selecting a result auto-fills the fields below.
      </HelperText>
      {open && (results.length > 0 || noMatches) && (
        <div className="absolute z-20 mt-1 w-full bg-paper border border-line rounded-md shadow-lg max-h-80 overflow-y-auto">
          {results.map((r) => (
            <button
              type="button"
              key={r.scorecard_id}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-forest-50 border-b border-line last:border-b-0"
            >
              <div className="font-medium text-sm text-ink">{r.name}</div>
              <div className="text-xs text-soft">
                {r.city ?? ''}{r.city && r.state ? ', ' : ''}{r.state ?? ''}
              </div>
            </button>
          ))}
          {noMatches && <div className="px-3 py-2 text-sm text-soft">No matches found.</div>}
        </div>
      )}
    </div>
  );
}
